import * as React from "react"
import { Switch as SwitchPrimitive, composeRenderProps, type SwitchProps } from "react-aria-components"

import { cn } from "@/lib/utils"

/**
 * Accessible on/off toggle over react-aria-components — the "Available for AR/VR" switch in the create-manual drawer
 * (MV6). react-aria puts `data-selected`/`data-disabled`/`data-focus-visible` on the label element, which drives the
 * track + thumb via `group-data-*`. Token-styled for both themes. `children` is the visible label text.
 */
function Switch({
  className,
  children,
  ...props
}: Omit<SwitchProps, "children"> & { className?: string; children?: React.ReactNode }) {
  return (
    <SwitchPrimitive
      {...props}
      className={composeRenderProps(className, (c) =>
        cn("group inline-flex items-center gap-2 text-sm data-[disabled]:opacity-50", c),
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          // OFF track = `bg-border` (a mid-grey visible on the card in BOTH themes — `bg-input` equals `--card` in dark
          // → invisible). ON track = primary.
          "bg-border group-data-[selected]:bg-primary relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors",
          "group-data-[focus-visible]:ring-ring/50 group-data-[focus-visible]:ring-3",
        )}
      >
        {/* Thumb stays high-contrast in all 4 theme×state combos: light thumb on the grey/dark-blue tracks (light), and
            a light thumb OFF / a dark (`primary-foreground`) thumb ON in dark (where `--primary` is a light brand). */}
        <span className="bg-background dark:bg-foreground dark:group-data-[selected]:bg-primary-foreground pointer-events-none ml-0.5 size-4 rounded-full shadow-sm transition-transform group-data-[selected]:translate-x-4" />
      </span>
      {children ? <span className="cursor-pointer select-none">{children}</span> : null}
    </SwitchPrimitive>
  )
}

export { Switch }
